import type { OsValue, SearchFacets, StatusValue } from '@contracts';
import { Building2, Check, ChevronDown, Clock, HeartPulse, Laptop, MapPin, Package, Plus, Search, Signal, X } from 'lucide-react';
import { Popover } from 'radix-ui';
import { type ReactNode, useMemo, useState } from 'react';
import type { Catalog } from '../ask/interpret';
import { timeZone } from '../lib/time';
import { describeSeen, type FilterState, type SeenFilter, signalGroups, signalLabels } from '../search/state';
import type { SignalKey } from '@api/devices/contracts';
import { cn } from '../ui/cn';
import { STATUS_ORDER, statusMeta } from '../ui/status';

export type FilterField = 'org' | 'site' | 'status' | 'os' | 'seen' | 'signal' | 'software' | 'duplicateName';

const OS_OPTIONS: Array<[OsValue, string]> = [['windows', 'Windows'], ['macos', 'macOS'], ['linux', 'Linux'], ['unknown', 'Not reported']];
const fieldMeta: Record<FilterField, { label: string; icon: typeof Building2 }> = {
  org: { label: 'Customer', icon: Building2 }, site: { label: 'Site', icon: MapPin }, status: { label: 'Status', icon: Signal },
  os: { label: 'OS', icon: Laptop }, seen: { label: 'Check-in', icon: Clock },
  signal: { label: 'Health', icon: HeartPulse }, software: { label: 'Software', icon: Package }, duplicateName: { label: 'Duplicate names', icon: Laptop },
};

export function FilterBar({ state, onChange, facets, catalog, fields, locked = [], trailing }: {
  state: FilterState; onChange: (next: FilterState) => void; facets?: SearchFacets; catalog: Catalog;
  fields: FilterField[]; locked?: FilterField[]; trailing?: ReactNode;
}) {
  const set = (patch: Partial<FilterState>) => onChange({ ...state, ...patch, page: 1 });
  const active = (f: FilterField) => f === 'duplicateName' ? state.duplicateName : f === 'seen' ? state.seen !== null : f === 'software' ? state.software.trim().length > 0 : state[f].length > 0;
  const name = (list: Array<{ id: string; title: string }>, id: string) => list.find(x => x.id === id)?.title ?? 'Unavailable';
  const summary: Record<FilterField, string> = {
    org: state.org.map(id => name(catalog.organizations, id)).join(', '),
    site: state.site.map(id => name(catalog.sites, id)).join(', '),
    status: state.status.map(s => statusMeta[s].label).join(', '),
    os: state.os.map(o => OS_OPTIONS.find(x => x[0] === o)![1]).join(', '),
    seen: state.seen ? describeSeen(state.seen).short : '',
    signal: state.signal.map(s => signalLabels[s]).join(', '),
    duplicateName: state.duplicateName ? 'Same customer' : '',
    software: state.software ? `contains “${state.software}”` : '',
  };
  const clear = (f: FilterField) => set(f === 'duplicateName' ? { duplicateName: false } : f === 'seen' ? { seen: null } : f === 'software' ? { software: '' } : { [f]: [] });
  const visible = fields.filter(f => !locked.includes(f));
  const anyActive = visible.some(active);

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filters">
      {visible.map(f => (
        <FilterChip key={f} field={f} active={active(f)} summary={summary[f]} onClear={() => clear(f)}>
          {f === 'org' && <EntityOptions options={catalog.organizations.map(o => ({ id: o.id, title: o.title }))}
            counts={facets?.organizations} selected={state.org} onChange={org => set({ org })} noun="customers" />}
          {f === 'site' && <EntityOptions noun="sites" counts={facets?.sites} selected={state.site} onChange={site => set({ site, kind: state.kind === 'organization' ? 'all' : state.kind })}
            options={catalog.sites.filter(s => !state.org.length || state.org.includes(s.organizationId))
              .map(s => ({ id: s.id, title: s.title, detail: name(catalog.organizations, s.organizationId) }))} />}
          {f === 'status' && <ValueOptions<StatusValue> selected={state.status} onChange={status => set({ status })}
            options={STATUS_ORDER.map(s => { const m = statusMeta[s], Icon = m.icon; return { value: s, label: s === 'unknown' ? 'Not reported' : m.label, icon: <Icon className={cn('size-4', m.text)} />, count: facets?.status[s] }; })} />}
          {f === 'os' && <ValueOptions<OsValue> selected={state.os} onChange={os => set({ os })}
            options={OS_OPTIONS.map(([value, label]) => ({ value, label, count: facets?.os[value] }))} />}
          {f === 'seen' && <SeenOptions value={state.seen} onChange={seen => set({ seen })} facets={facets} />}
          {f === 'signal' && <SignalOptions selected={state.signal} counts={facets?.signals} onChange={signal => set({ signal })} />}
          {f === 'duplicateName' && <OptionRow checked={state.duplicateName} onToggle={() => set({ duplicateName: !state.duplicateName })}>Same name within a customer</OptionRow>}
          {f === 'software' && <SoftwareOption value={state.software} onChange={software => set({ software })} />}
        </FilterChip>
      ))}
      {anyActive && (
        <button type="button" onClick={() => onChange({ ...state, org: locked.includes('org') ? state.org : [], site: locked.includes('site') ? state.site : [], status: [], os: [], seen: null, signal: [], software: '', duplicateName: false, page: 1 })}
          className="h-7 rounded-md px-2 text-[13px] font-medium text-ink-3 hover:bg-hover hover:text-ink">Clear filters</button>
      )}
      {trailing && <><span className="flex-1" />{trailing}</>}
    </div>
  );
}

function FilterChip({ field, active, summary, onClear, children }: {
  field: FilterField; active: boolean; summary: string; onClear: () => void; children: ReactNode;
}) {
  const { label, icon: Icon } = fieldMeta[field];
  return (
    <span className={cn('inline-flex h-7 items-center rounded-md border text-[13px] transition-colors',
      active ? 'border-brand/35 bg-brand-soft text-ink' : 'border-dashed border-line-strong text-ink-2 hover:border-ink-3 hover:text-ink')}>
      <Popover.Root>
        <Popover.Trigger className="inline-flex h-full max-w-[min(340px,calc(100vw-5rem))] items-center gap-1.5 rounded-md pl-2 pr-2 outline-offset-1">
          {active ? <Icon className="size-3.5 shrink-0 text-brand-ink" /> : <Plus className="size-3.5 shrink-0" />}
          <span className={cn('shrink-0', active && 'text-ink-2')}>{label}{active ? ':' : ''}</span>
          {active && <span className="truncate font-medium">{summary}</span>}
          <ChevronDown className="size-3.5 shrink-0 opacity-60" />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="start" sideOffset={6} collisionPadding={12}
            className="z-50 w-[min(320px,calc(100vw-1.5rem))] rounded-xl border border-line bg-raised p-1.5 shadow-pop animate-pop">
            {children}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {active && (
        <button type="button" onClick={onClear} aria-label={`Remove ${label} filter`}
          className="mr-0.5 inline-flex size-6 items-center justify-center rounded text-ink-3 hover:bg-hover hover:text-ink"><X className="size-3.5" /></button>
      )}
    </span>
  );
}

function OptionRow({ checked, onToggle, children, count, radio }: { checked: boolean; onToggle: () => void; children: ReactNode; count?: number; radio?: boolean }) {
  return (
    <button type="button" role={radio ? 'radio' : 'checkbox'} aria-checked={checked} onClick={onToggle}
      className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-hover', count === 0 && !checked && 'opacity-55')}>
      <span className={cn('flex size-4 shrink-0 items-center justify-center border', radio ? 'rounded-full' : 'rounded',
        checked ? 'border-brand bg-brand text-white' : 'border-line-strong bg-panel')}>
        {checked && (radio ? <span className="size-1.5 rounded-full bg-white" /> : <Check className="size-3" strokeWidth={3} />)}
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-2">{children}</span>
      {count !== undefined && <span className="tabular text-[12px] text-ink-3">{count}</span>}
    </button>
  );
}

function ValueOptions<T extends string>({ options, selected, onChange }: {
  options: Array<{ value: T; label: string; icon?: ReactNode; count?: number }>; selected: T[]; onChange: (next: T[]) => void;
}) {
  return (
    <div role="group">
      {options.map(o => (
        <OptionRow key={o.value} checked={selected.includes(o.value)} count={o.count}
          onToggle={() => onChange(selected.includes(o.value) ? selected.filter(v => v !== o.value) : [...selected, o.value])}>
          {o.icon}<span className="truncate">{o.label}</span>
        </OptionRow>
      ))}
      <p className="px-2 pb-1 pt-1.5 text-[11.5px] text-ink-3">Counts show how many match your other filters.</p>
    </div>
  );
}

function EntityOptions({ options, counts, selected, onChange, noun }: {
  options: Array<{ id: string; title: string; detail?: string }>; counts?: Array<{ id: string; count: number }>;
  selected: string[]; onChange: (next: string[]) => void; noun: string;
}) {
  const [text, setText] = useState('');
  const countOf = useMemo(() => new Map(counts?.map(c => [c.id, c.count])), [counts]);
  const shown = options.filter(o => `${o.title} ${o.detail ?? ''}`.toLowerCase().includes(text.trim().toLowerCase()));
  // Keep selections reachable even if the catalogue no longer lists them.
  const missing = selected.filter(id => !options.some(o => o.id === id));
  return (
    <div role="group">
      {options.length > 7 && (
        <label className="mb-1 flex items-center gap-2 border-b border-line px-2 pb-2 pt-1">
          <Search className="size-3.5 text-ink-3" />
          <input autoFocus value={text} onChange={e => setText(e.target.value)} placeholder={`Find ${noun}`} className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-ink-3" />
        </label>
      )}
      <div className="max-h-72 overflow-y-auto">
        {shown.map(o => (
          <OptionRow key={o.id} checked={selected.includes(o.id)} count={counts ? countOf.get(o.id) ?? 0 : undefined}
            onToggle={() => onChange(selected.includes(o.id) ? selected.filter(v => v !== o.id) : [...selected, o.id])}>
            <span className="min-w-0"><span className="block truncate">{o.title}</span>{o.detail && <span className="block truncate text-[12px] text-ink-3">{o.detail}</span>}</span>
          </OptionRow>
        ))}
        {missing.map(id => (
          <OptionRow key={id} checked onToggle={() => onChange(selected.filter(v => v !== id))}><span className="italic text-ink-3">No longer available</span></OptionRow>
        ))}
        {!shown.length && !missing.length && <p className="px-2 py-3 text-center text-ink-3">No {noun} found</p>}
      </div>
    </div>
  );
}

const PRESETS: Array<{ key: string; label: string; value: SeenFilter; count?: (f: SearchFacets) => number }> = [
  { key: 'h1', label: 'Within the last hour', value: { type: 'within', amount: 1, unit: 'h' } },
  { key: 'd1', label: 'Within 24 hours', value: { type: 'within', amount: 1, unit: 'd' }, count: f => f.lastSeen.day },
  { key: 'd7', label: 'Within 7 days', value: { type: 'within', amount: 7, unit: 'd' }, count: f => f.lastSeen.day + f.lastSeen.week },
  { key: 'o7', label: 'Not for 7+ days, or never', value: { type: 'older', amount: 7, unit: 'd', includeNever: true }, count: f => f.lastSeen.older + f.lastSeen.unknown },
  { key: 'o30', label: 'Not for 30+ days, or never', value: { type: 'older', amount: 30, unit: 'd', includeNever: true } },
  { key: 'never', label: 'Never checked in', value: { type: 'never' }, count: f => f.lastSeen.unknown },
];
const same = (a: SeenFilter | null, b: SeenFilter) => JSON.stringify(a) === JSON.stringify(b);
const toLocalInput = (iso: string) => { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16); };

function SeenOptions({ value, onChange, facets }: { value: SeenFilter | null; onChange: (seen: SeenFilter | null) => void; facets?: SearchFacets }) {
  const custom = value && (value.type === 'before' || value.type === 'after') ? value : null;
  const [mode, setMode] = useState<'before' | 'after'>(custom?.type ?? 'before');
  const [at, setAt] = useState(custom ? toLocalInput(custom.at) : '');
  const [includeNever, setIncludeNever] = useState(custom?.type === 'before' ? custom.includeNever : false);
  const applyCustom = (m = mode, local = at, never = includeNever) => {
    if (!local || Number.isNaN(new Date(local).getTime())) return;
    const iso = new Date(local).toISOString();
    onChange(m === 'before' ? { type: 'before', at: iso, includeNever: never } : { type: 'after', at: iso });
  };
  return (
    <div role="group">
      {PRESETS.map(p => (
        <OptionRow key={p.key} radio checked={same(value, p.value)} count={facets && p.count ? p.count(facets) : undefined}
          onToggle={() => onChange(same(value, p.value) ? null : p.value)}>{p.label}</OptionRow>
      ))}
      <div className="mt-1 border-t border-line px-2 pb-1 pt-2.5">
        <div className="flex items-center gap-1.5">
          <select value={mode} onChange={e => { const m = e.target.value as 'before' | 'after'; setMode(m); applyCustom(m); }}
            aria-label="Custom check-in comparison" className="h-8 rounded-md border border-line bg-panel px-1.5 text-[13px]">
            <option value="before">Before</option><option value="after">After</option>
          </select>
          <input type="datetime-local" value={at} aria-label="Custom check-in time" onChange={e => { setAt(e.target.value); applyCustom(mode, e.target.value); }}
            className="h-8 min-w-0 flex-1 rounded-md border border-line bg-panel px-1.5 text-[13px]" />
        </div>
        {mode === 'before' && (
          <label className="mt-2 flex items-center gap-2 text-[13px] text-ink-2">
            <input type="checkbox" checked={includeNever} onChange={e => { setIncludeNever(e.target.checked); applyCustom(mode, at, e.target.checked); }} className="accent-[var(--c-brand)]" />
            Include devices that never checked in
          </label>
        )}
      </div>
      <p className="px-2 pb-1 pt-2 text-[11.5px] leading-snug text-ink-3">
        {value ? <><span className="font-medium text-ink-2">{describeSeen(value).exact}.</span> </> : null}
        Times are in {timeZone()}. A device that never checked in is unknown, not offline.
      </p>
    </div>
  );
}

function SignalOptions({ selected, counts, onChange }: { selected: SignalKey[]; counts?: Partial<Record<SignalKey, number>>; onChange: (next: SignalKey[]) => void }) {
  return (
    <div role="group" className="max-h-80 overflow-y-auto">
      {signalGroups.map(([group, keys]) => (
        <div key={group}>
          <p className="px-2 pb-0.5 pt-2 text-[11.5px] font-semibold uppercase tracking-wide text-ink-3">{group}</p>
          {keys.map(k => (
            <OptionRow key={k} checked={selected.includes(k)} count={counts ? counts[k] ?? 0 : undefined}
              onToggle={() => onChange(selected.includes(k) ? selected.filter(v => v !== k) : [...selected, k])}>{signalLabels[k]}</OptionRow>
          ))}
        </div>
      ))}
      <p className="px-2 pb-1 pt-1.5 text-[11.5px] text-ink-3">Open signals from devices with the agent. Devices without it never match.</p>
    </div>
  );
}

function SoftwareOption({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [text, setText] = useState(value);
  const apply = () => onChange(text.trim().length >= 2 ? text.trim() : '');
  return (
    <form className="space-y-2 p-1.5" onSubmit={e => { e.preventDefault(); apply(); }}>
      <label className="block text-[12.5px] text-ink-2">Installed software whose name contains
        <input autoFocus value={text} onChange={e => setText(e.target.value)} maxLength={100} placeholder="e.g. Zoom, 7-Zip, Acrobat"
          className="mt-1 h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50" /></label>
      <div className="flex justify-end"><button type="submit" className="h-8 rounded-md bg-ink px-3 text-[13px] font-medium text-canvas">Apply</button></div>
      <p className="text-[11.5px] text-ink-3">From the agent’s software inventory; devices without the agent never match.</p>
    </form>
  );
}
